/**
 * GA4Form Component
 *
 * This form component handles Google Analytics 4 connector setup.
 * It fetches available GA4 properties from the API endpoint:
 * /company-connector/ga4/{id}/properties
 *
 * The form displays a searchable dropdown containing:
 * - Account Name
 * - Property Name
 * - Property ID
 *
 * Users can search/filter properties by name and select the desired GA4 property to connect.
 */

import React, { useState, useMemo } from "react"
import { toast } from "react-toastify"

import { ConnectorDialog } from "~/ui-rtk/components/features/connectors"
import { useConnect } from "./connect"

import {
  isInternalServerError,
  isUnauthorizedError,
} from "~/ui-rtk/utils/http-utils"

import { connectorIcons } from "~/ui-rtk/shared/constants/image-paths.contants"
import { SearchSelect } from "~/ui-rtk/components/ui/common"
import type { Option } from "~/ui-rtk/components/ui/common/SearchSelect"
import { Button } from "~/ui-rtk/components/ui/controls"

import type { CustomerConnectorDialogFormPropsWithoutUri } from "../types"

type GA4FormProps = CustomerConnectorDialogFormPropsWithoutUri

const GA4Form: React.FC<GA4FormProps> = ({
  companyConnectorId,
  isOpen,
  onClose,
}) => {
  const {
    isLoading,
    setProperty,
    properties,
    isLoadingProperties,
    propertiesError,
  } = useConnect(companyConnectorId)

  const [selectedProperty, setSelectedProperty] = useState<Option | undefined>(
    undefined,
  )

  // Convert properties to search select options
  const propertyOptions = useMemo(
    () =>
      properties.map(property => ({
        label: `${property.propertyName} (${property.propertyId})`,
        value: property.propertyId,
      })),
    [properties],
  )

  const handlePropertySelect = (option: Option) => {
    setSelectedProperty(option)
  }

  const handleConnect = async () => {
    if (!companyConnectorId) {
      throw new Error("Missing company connector id")
    }

    if (!selectedProperty?.value) {
      toast.error("Please select a GA4 property", {
        toastId: "ga4-form-no-property-selected",
      })
      return
    }

    try {
      await setProperty(companyConnectorId, String(selectedProperty.value))
      toast.success("GA4 property connected successfully!", {
        toastId: "ga4-form-connect-success",
      })
      onClose()
    } catch (err) {
      if (isUnauthorizedError(err)) {
        toast.error("Failed to set GA4 property", {
          toastId: "ga4-form-connect-unauthorized-error",
        })
      } else if (isInternalServerError(err)) {
        toast.error("Uh oh! Something didn't go as expected!", {
          toastId: "ga4-form-connect-interval-error",
        })
      } else {
        toast.error("Uh oh! The server returned a bad response!", {
          toastId: "ga4-form-connect-bad-response-error",
        })
      }
    }
  }

  return (
    <ConnectorDialog
      imgSrc={connectorIcons.googleAnalytics4}
      alt="Google Analytics 4 Logo"
      title="Connection to Google Analytics 4"
      isOpen={isOpen}
      onClose={onClose}
      scrollable={false}
    >
      {/* Show authentication needed if no properties available */}
      {!isLoadingProperties && !propertiesError && properties.length === 0 ? (
        <div className="flex flex-col w-full gap-4 mt-8">
          <p className="leading-5 text-left text-sm text-platinum">
            You need to authenticate with Google Analytics 4 first to access
            your properties.
          </p>

          <Button
            variant={{ variant: "solid", color: "white" }}
            onClick={() =>
              (window.location.href = `/company-connector/ga4/${companyConnectorId}/auth`)
            }
            className="w-full"
          >
            Authenticate with Google Analytics 4
          </Button>
        </div>
      ) : (
        <>
          <p className="leading-5 text-left text-sm text-platinum">
            Select the Google Analytics 4 property you want to connect to
            Marathon. Make sure you have the necessary permissions to access the
            selected property.
          </p>

          <div className="flex flex-col w-full gap-4 mt-8">
            <div className="space-y-3">
              <div className="flex flex-col gap-2 justify-start">
                <label className="text-base font-medium text-white text-left">
                  GA4 Property*
                </label>

                {propertiesError && (
                  <div className="flex flex-col gap-3">
                    <div className="text-error text-sm">{propertiesError}</div>
                    <Button
                      variant={{ variant: "solid", color: "blue" }}
                      onClick={() =>
                        (window.location.href = `/company-connector/ga4/${companyConnectorId}/auth`)
                      }
                      className="w-full"
                    >
                      Re-authenticate with Google Analytics 4
                    </Button>
                  </div>
                )}
                {!propertiesError && isLoadingProperties && (
                  <div className="text-platinum text-sm">
                    Loading properties...
                  </div>
                )}
                {!propertiesError && !isLoadingProperties && (
                  <SearchSelect
                    options={propertyOptions}
                    value={selectedProperty}
                    onSelect={handlePropertySelect}
                    classes={{
                      container: "w-full",
                      input: selectedProperty
                        ? "text-white"
                        : "text-cadet-blue",
                    }}
                  />
                )}

                {selectedProperty && (
                  <div className="text-xs text-cadet-blue mt-1">
                    Selected: {selectedProperty.label}
                  </div>
                )}

                {isLoading && (
                  <div className="text-blue-400 text-sm mt-2">
                    Connecting to GA4 property...
                  </div>
                )}
              </div>
            </div>

            <Button
              variant={{ variant: "solid", color: "white" }}
              onClick={handleConnect}
              isLoading={isLoading}
              disabled={
                !selectedProperty || isLoadingProperties || !!propertiesError
              }
              className="w-full"
            >
              Connect
            </Button>
          </div>
        </>
      )}
    </ConnectorDialog>
  )
}

export default GA4Form
